import { useEffect, useState } from 'react';
import { AlertBannerType, ExAlertBanner, ExEmptyState, ExLoader } from '@boomi/exosphere';
import { getConnectors, type Connector } from './connectors';
import ConnectorRow from './ConnectorRow';
import SearchInput from '../shared/SearchInput';
import styles from './ConnectorList.module.css';
import { GENERIC_ERROR_MESSAGE } from '../shared/apiUtils';

interface ConnectorListProps {
    onSelect: (connectorId: string) => void;
}

const ConnectorList = ({ onSelect }: ConnectorListProps) => {
    const [connectors, setConnectors] = useState<Connector[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [search, setSearch] = useState('');

    useEffect(() => {
        const load = async () => {
            try {
                const data = await getConnectors();
                setConnectors(data.sort((a, b) => a.name.localeCompare(b.name)));
            } catch (err) {
                setError(err instanceof Error ? err.message : GENERIC_ERROR_MESSAGE);
            } finally {
                setIsLoading(false);
            }
        };

        load();
    }, []);

    const filteredConnectors = search
        ? connectors.filter((c) => c.name.toLowerCase().includes(search.toLowerCase()))
        : connectors;

    return (
        <section className={styles.section}>
            {isLoading ? (
                <ExLoader label="Loading connectors…" />
            ) : error ? (
                <ExAlertBanner type={AlertBannerType.ERROR} open>
                    {error}
                </ExAlertBanner>
            ) : (
                <>
                    <SearchInput
                        value={search}
                        placeholder="Search connectors"
                        onSearch={setSearch}
                    />
                    {filteredConnectors.length === 0 ? (
                        <ExEmptyState
                            className={styles.noResults}
                            label="No Results"
                            text={`No connectors found for "${search}"`}
                        >
                            <div slot="header">
                                <img src="/emptyStateIcon.svg" alt="No Results" />
                            </div>
                        </ExEmptyState>
                    ) : (
                        <div className={styles.track}>
                            {filteredConnectors.map((connector) => (
                                <ConnectorRow
                                    key={connector.id}
                                    connector={connector}
                                    onViewConnections={onSelect}
                                />
                            ))}
                        </div>
                    )}
                </>
            )}
        </section>
    );
};

export default ConnectorList;
